import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { DOCUMENT_CHECK_MESSAGES, MAX_DOCUMENT_BYTES, sniffContentType } from '@wodi/domain';
import type { StaffDocumentView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  FileField,
  formatFileSize,
  LoadingBlock,
  Notice,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { fileToBase64 } from '../api/clinical-documents.ts';
import {
  fetchStaffDocumentContent,
  staffDocumentEnteredInError,
  staffKeys,
  uploadStaffDocument,
} from '../api/staff.ts';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './format.tsx';
import styles from './staff.module.css';

// Licence copies on a staff record (docs/plans/phase2-staff-records.md §2.3, §6; ADR 0016):
// PDF, JPG or PNG up to 10 MB. The server decides the type from the bytes; the check here
// only answers sooner. Opening is audited as a read, "Download" as an export. A wrong upload
// is marked entered in error with a reason and stays in the list.

const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png', '.pdf', '.jpg', '.jpeg', '.png'];
const SNIFF_BYTES = 16;

async function checkFile(file: File): Promise<string | null> {
  if (file.size === 0) return DOCUMENT_CHECK_MESSAGES['document.empty'];
  if (file.size > MAX_DOCUMENT_BYTES) return DOCUMENT_CHECK_MESSAGES['document.too_large'];
  const head = new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer());
  return sniffContentType(head) === null
    ? DOCUMENT_CHECK_MESSAGES['document.type_not_allowed']
    : null;
}

function UploadDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const upload = useMutation({
    mutationFn: async () => {
      if (file === null) throw new Error('no file');
      return uploadStaffDocument(userId, {
        clientRequestId,
        kind: 'licence',
        contentBase64: await fileToBase64(file),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
      toasts.show({ title: 'Licence copy uploaded' });
      onClose();
    },
  });
  const missing = file === null ? 'Choose the scanned licence.' : null;
  return (
    <Dialog
      title="Upload licence copy"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <FileField
          label="Licence"
          description="A scan or photo of the current practising licence. PDF, JPG or PNG, up to 10 MB."
          acceptedFileTypes={ACCEPTED}
          file={file}
          onChange={(next) => {
            setFile(next);
            setFileError(null);
            if (next !== null) void checkFile(next).then(setFileError);
          }}
          {...(fileError !== null
            ? { errorMessage: fileError }
            : tried && missing !== null
              ? { errorMessage: missing }
              : {})}
        />
        {upload.isError ? (
          <Notice tone="error">
            {errorText(upload.error, "The copy wasn't uploaded. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={upload.isPending}
          pendingLabel="Uploading…"
          onPress={() => {
            setTried(true);
            if (missing !== null || fileError !== null) return;
            upload.mutate();
          }}
        >
          Upload licence copy
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const contentKey = (id: string) => ['staff', 'documents', id, 'content'] as const;

function releaseUrl(queryClient: QueryClient, id: string): void {
  const url = queryClient.getQueryData<string>(contentKey(id));
  if (url !== undefined) URL.revokeObjectURL(url);
  queryClient.removeQueries({ queryKey: contentKey(id) });
}

function Viewer({ doc }: { doc: StaffDocumentView }) {
  const content = useQuery({
    queryKey: contentKey(doc.id),
    queryFn: async () =>
      URL.createObjectURL(
        new Blob([await fetchStaffDocumentContent(doc.id, 'view')], { type: doc.contentType }),
      ),
    staleTime: Infinity,
    retry: false,
  });
  if (content.isPending) return <LoadingBlock label="Opening the licence copy" />;
  if (content.isError)
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void content.refetch()}>Try again</Button>}
      >
        {errorText(content.error, 'The licence copy could not be opened. Try again.')}
      </Notice>
    );
  return doc.contentType === 'application/pdf' ? (
    <iframe className={styles.viewer} src={content.data} title="Licence copy" />
  ) : (
    <img className={styles.viewerImage} src={content.data} alt="Licence copy" />
  );
}

async function download(doc: StaffDocumentView): Promise<void> {
  const blob = await fetchStaffDocumentContent(doc.id, 'download');
  const url = URL.createObjectURL(new Blob([blob], { type: doc.contentType }));
  const link = document.createElement('a');
  link.href = url;
  const extension =
    doc.contentType === 'application/pdf' ? 'pdf' : doc.contentType === 'image/png' ? 'png' : 'jpg';
  link.download = `licence-${doc.id}.${extension}`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function LicenceCopies({
  userId,
  documents,
  mayManage,
  online,
}: {
  userId: string;
  documents: readonly StaffDocumentView[];
  mayManage: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState<StaffDocumentView | null>(null);
  const [correcting, setCorrecting] = useState<StaffDocumentView | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const correct = useMutation({
    mutationFn: (reason: string) => {
      if (correcting === null) throw new Error('nothing chosen');
      return staffDocumentEnteredInError(correcting.id, reason);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
      toasts.show({ title: 'Marked entered in error' });
      setCorrecting(null);
    },
  });
  const closeViewer = () => {
    if (viewing !== null) releaseUrl(queryClient, viewing.id);
    setViewing(null);
  };

  return (
    <>
      {mayManage ? (
        <div className={styles.actions}>
          <Button
            isDisabled={!online}
            onPress={() => {
              setUploading(true);
            }}
          >
            Upload licence copy
          </Button>
          {online ? null : <p className={styles.meta}>Uploading needs a connection to Wodi.</p>}
        </div>
      ) : null}
      {downloadError === null ? null : <Notice tone="error">{downloadError}</Notice>}
      {documents.length === 0 ? (
        <p className={styles.meta}>No licence copy uploaded yet.</p>
      ) : (
        <ul className={styles.list} aria-label="Licence copies">
          {documents.map((doc) => {
            const error = doc.enteredInError;
            return (
              <li key={doc.id} className={styles.item}>
                <div className={styles.itemMain}>
                  <span>{`Licence copy, ${formatFileSize(doc.sizeBytes)}`}</span>
                  <span className={styles.meta}>
                    {`Uploaded ${formatInstant(doc.uploadedAt)} by ${doc.uploadedBy.displayName}`}
                  </span>
                  {error === null ? null : (
                    <span className={styles.meta}>
                      {`Entered in error ${formatInstant(error.at)} by ${error.by.displayName}: ${error.reason}`}
                    </span>
                  )}
                </div>
                {error === null ? null : <Tag state="neutral">Entered in error</Tag>}
                <div className={styles.actions}>
                  <Button
                    variant="secondary"
                    isDisabled={!online}
                    onPress={() => {
                      setViewing(doc);
                    }}
                  >
                    Open
                  </Button>
                  <Button
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      setDownloadError(null);
                      download(doc).catch((e: unknown) => {
                        setDownloadError(errorText(e, 'The download failed. Try again.'));
                      });
                    }}
                  >
                    Download
                  </Button>
                  {mayManage && error === null ? (
                    <Button
                      variant="quiet"
                      isDisabled={!online}
                      onPress={() => {
                        setCorrecting(doc);
                      }}
                    >
                      Entered in error
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {uploading ? (
        <UploadDialog
          userId={userId}
          onClose={() => {
            setUploading(false);
          }}
        />
      ) : null}
      {viewing === null ? null : (
        <Dialog
          title="Licence copy"
          isOpen
          onOpenChange={(open) => {
            if (!open) closeViewer();
          }}
        >
          <Viewer doc={viewing} />
          <DialogActions>
            <Button onPress={closeViewer}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {correcting === null ? null : (
        <ReasonDialog
          title="Mark licence copy entered in error"
          isOpen
          onOpenChange={(open) => {
            if (!open) setCorrecting(null);
          }}
          confirmLabel="Mark entered in error"
          pendingLabel="Saving…"
          isPending={correct.isPending}
          error={correct.isError ? errorText(correct.error, "It wasn't saved. Try again.") : null}
          onConfirm={(reason) => {
            correct.mutate(reason);
          }}
        />
      )}
    </>
  );
}
